/**
 * EmailAuthPanel.jsx — Panel EMAIL_LOGIN / EMAIL_REGISTER.
 *
 * Deux formulaires dans un seul panel, bascule via onglets internes :
 *   - Connexion  (EMAIL_LOGIN)   : email + mot de passe + toggle visibilité
 *   - Inscription (EMAIL_REGISTER) : nom d'utilisateur + email + mot de passe
 *                                    + confirmation + jauge de force
 *
 * Toutes les erreurs sont affichées en inline (jamais de navigation sur erreur).
 * La navigation est pilotée par le dispatch (EMAIL_AUTH_SUCCESS → SUCCESS,
 * BACK → PICKING, SWITCH_TO_REGISTER / SWITCH_TO_LOGIN).
 *
 * Règles DA respectées :
 * §14 — Zéro import @mui/@material-ui.
 * §20 — Zéro littéral couleur en dur.
 * §18 ter — ≥1 breakpoint (hérité des styled-components partagés).
 * §12 undecies — Casse grammaticale (pas d'UPPERCASE forcé).
 * §8.7 — Aucun import Redux.
 *
 * Props consommées :
 *   currentState  {string}   — 'EMAIL_LOGIN' | 'EMAIL_REGISTER'
 *   dispatch      {function} — dispatch de useWalletStateMachine
 *   emailAuth     {object}   — valeur de retour de useEmailAuth()
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveReferralCode } from '../../../services/ReferralService';
import { ACTION_TYPES } from '../hooks/useWalletStateMachine';
import {
  getPasswordStrength,
  PASSWORD_STRENGTH_LABELS,
} from '../hooks/useEmailAuth';
import {
  WalletBody,
  WalletFooter,
  FormGroup,
  FormLabel,
  FormInput,
  FormInputWrap,
  PasswordToggle,
  InlineError,
  PasswordStrengthBar,
  PasswordStrengthFill,
  PasswordStrengthLabel,
  PrimaryBtn,
  BackLink,
  ToSText,
} from '../styles/EarniWalletModal.styles';
import { Spinner } from '../../ui/ModalDA';
import { COLORS } from '../../ui/theme';

/* ─── Styles banner parrainage ───────────────────────────────────────────────── */
const referralBannerStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '0.5rem',
  backgroundColor: 'rgba(194,24,166,0.08)',
  border: '1px solid rgba(194,24,166,0.25)',
  borderRadius: '0.5rem',
  padding: '0.75rem 1rem',
  marginBottom: '1rem',
  fontSize: '0.875rem',
  color: '#0d0520',
  lineHeight: '1.4',
};
const referralGiftIcon = { fontSize: '1rem', flexShrink: 0, marginTop: '0.05rem' };

/* ─── Icônes SVG inline ──────────────────────────────────────────────────────── */

/** Icône flèche retour. */
const BackArrow = () => (
  <svg width="0.875rem" height="0.875rem" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M19 12H5" />
    <path d="m12 5-7 7 7 7" />
  </svg>
);

/** Œil ouvert — afficher le mot de passe. */
const EyeOpen = () => (
  <svg width="1rem" height="1rem" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

/** Œil barré — masquer le mot de passe. */
const EyeClosed = () => (
  <svg width="1rem" height="1rem" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

/* ─── Champ mot de passe avec toggle visibilité ─────────────────────────────── */

/**
 * PasswordField — input + bouton toggle visibilité.
 *
 * @param {object} props
 * @param {string}   props.id
 * @param {string}   props.value
 * @param {function} props.onChange
 * @param {string}   props.placeholder
 * @param {boolean}  props.$hasError
 * @param {string}   props['aria-describedby']
 */
function PasswordField({ id, value, onChange, placeholder, $hasError, ...rest }) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  return (
    <FormInputWrap>
      <FormInput
        id={id}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        $hasError={$hasError}
        autoComplete={id === 'signInPassword' ? 'current-password' : 'new-password'}
        style={{ paddingRight: '2.5rem' }}
        {...rest}
      />
      <PasswordToggle
        type="button"
        tabIndex={-1}
        aria-label={visible ? t('HidePassword') : t('ShowPassword')}
        onClick={() => setVisible((v) => !v)}
      >
        {visible ? <EyeClosed /> : <EyeOpen />}
      </PasswordToggle>
    </FormInputWrap>
  );
}

/* ─── Jauge de force du mot de passe ────────────────────────────────────────── */

/**
 * PasswordStrength — barre de progression + libellé.
 *
 * @param {object} props
 * @param {string} props.password — mot de passe à évaluer
 */
function PasswordStrength({ password }) {
  const { t } = useTranslation();
  if (!password) return null;
  const level   = getPasswordStrength(password);
  const percent = ((level + 1) / 4) * 100;
  const label   = PASSWORD_STRENGTH_LABELS[level];

  return (
    <div aria-live="polite" aria-atomic="true">
      <PasswordStrengthBar>
        <PasswordStrengthFill $percent={percent} $level={level} />
      </PasswordStrengthBar>
      <PasswordStrengthLabel>
        {t('PasswordStrengthPrefix')}: {label}
      </PasswordStrengthLabel>
    </div>
  );
}

/* ─── Formulaire Connexion ───────────────────────────────────────────────────── */

/**
 * SignInForm
 *
 * @param {object} props
 * @param {object}   props.emailAuth — hook useEmailAuth
 * @param {function} props.onBack
 * @param {function} props.onSwitchToRegister
 * @param {function} props.onForgotPassword — bascule vers le panel FORGOT_PASSWORD
 */
function SignInForm({ emailAuth, onBack, onSwitchToRegister, onForgotPassword }) {
  const { t } = useTranslation();
  const {
    signInEmail,    setSignInEmail,
    signInPassword, setSignInPassword,
    signInLoading,
    signInError,
    handleSignIn,
  } = emailAuth;

  /* Soumission via Entrée */
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') handleSignIn();
  }, [handleSignIn]);

  return (
    <form
      noValidate
      onSubmit={(e) => { e.preventDefault(); handleSignIn(); }}
      aria-label={t('SignInForm')}
    >
      {/* Champ email */}
      <FormGroup>
        <FormLabel htmlFor="signInEmail">{t('EmailAddress')}</FormLabel>
        <FormInput
          id="signInEmail"
          type="email"
          value={signInEmail}
          onChange={(e) => setSignInEmail(e.target.value)}
          placeholder={t('EmailPlaceholder')}
          $hasError={!!signInError}
          autoComplete="email"
          onKeyDown={handleKeyDown}
        />
      </FormGroup>

      {/* Champ mot de passe */}
      <FormGroup style={{ marginTop: '0.75rem' }}>
        <FormLabel htmlFor="signInPassword">{t('Password')}</FormLabel>
        <PasswordField
          id="signInPassword"
          value={signInPassword}
          onChange={(e) => setSignInPassword(e.target.value)}
          placeholder="••••••••"
          $hasError={!!signInError}
          onKeyDown={handleKeyDown}
        />
      </FormGroup>

      {/* Lien mot de passe oublié */}
      <div style={{ textAlign: 'right', marginTop: '0.375rem' }}>
        <button
          type="button"
          onClick={onForgotPassword}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            color: COLORS.accent,
            fontWeight: 600,
            fontSize: '0.8125rem',
            textDecoration: 'underline',
          }}
        >
          {t('ForgotPassword')}
        </button>
      </div>

      {/* Erreur inline */}
      {signInError && (
        <InlineError role="alert" aria-live="polite" style={{ marginTop: '0.5rem' }}>
          {signInError}
        </InlineError>
      )}

      {/* Bouton de connexion */}
      <PrimaryBtn
        type="submit"
        disabled={signInLoading || !signInEmail || !signInPassword}
        style={{ marginTop: '1.25rem' }}
        aria-busy={signInLoading}
      >
        {signInLoading ? <Spinner style={{ marginBottom: 0, width: '1.25rem', height: '1.25rem' }} /> : null}
        {signInLoading ? t('SigningIn') : t('SignInAction')}
      </PrimaryBtn>

      {/* Lien inscription */}
      <p style={{ textAlign: 'center', marginTop: '0.75rem', fontSize: '0.8125rem' }}>
        {t('NoAccountYet')}{' '}
        <button
          type="button"
          onClick={onSwitchToRegister}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            color: 'inherit',
            fontWeight: 700,
            textDecoration: 'underline',
            fontSize: 'inherit',
          }}
        >
          {t('CreateAccount')}
        </button>
      </p>
    </form>
  );
}

/* ─── Formulaire Inscription ─────────────────────────────────────────────────── */

/**
 * RegisterForm
 *
 * @param {object} props
 * @param {object}   props.emailAuth — hook useEmailAuth
 * @param {function} props.onSwitchToLogin
 */
function RegisterForm({ emailAuth, onSwitchToLogin }) {
  const { t } = useTranslation();
  const {
    username,          setUsername,
    signUpEmail,       setSignUpEmail,
    signUpPassword,    setSignUpPassword,
    confirmPassword,   setConfirmPassword,
    signUpLoading,
    signUpError,
    handleSignUp,
  } = emailAuth;

  const [referrerUsername, setReferrerUsername] = useState(null);

  // Détecter ?ref= dans l'URL et résoudre le code pour afficher le banner d'invitation
  useEffect(() => {
    const refCode = new URLSearchParams(window.location.search).get('ref');
    if (!refCode) return;
    resolveReferralCode(refCode)
      .then(data => { if (data && data.username) setReferrerUsername(data.username); })
      .catch(() => { /* Code invalide → pas de banner */ });
  }, []);

  return (
    <form
      noValidate
      onSubmit={(e) => { e.preventDefault(); handleSignUp(); }}
      aria-label={t('CreateAccountForm')}
    >
      {/* Banner parrainage */}
      {referrerUsername && (
        <div style={referralBannerStyle}>
          <span style={referralGiftIcon}>🎁</span>
          <span>
            <strong>@{referrerUsername}</strong> invited you to <strong>Earniverse</strong>, the AAA VR metaverse.
            Join <strong>Earnimarket</strong>, its collectibles marketplace, and you both earn{' '}
            <strong>50 earnicoins</strong>, up to <strong>150</strong> when you link Epic Games.
          </span>
        </div>
      )}

      {/* Nom d'utilisateur */}
      <FormGroup>
        <FormLabel htmlFor="regUsername">{t('Username')}</FormLabel>
        <FormInput
          id="regUsername"
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder={t('UsernamePlaceholder')}
          $hasError={!!signUpError}
          autoComplete="username"
          aria-describedby="usernameHint"
        />
        <span
          id="usernameHint"
          style={{ fontSize: '0.6875rem', color: 'rgba(13,10,16,0.55)', marginTop: '0.125rem' }}
        >
          {t('UsernameHint')}
        </span>
      </FormGroup>

      {/* Email */}
      <FormGroup style={{ marginTop: '0.75rem' }}>
        <FormLabel htmlFor="regEmail">{t('EmailAddress')}</FormLabel>
        <FormInput
          id="regEmail"
          type="email"
          value={signUpEmail}
          onChange={(e) => setSignUpEmail(e.target.value)}
          placeholder={t('EmailPlaceholder')}
          $hasError={!!signUpError}
          autoComplete="email"
        />
      </FormGroup>

      {/* Mot de passe */}
      <FormGroup style={{ marginTop: '0.75rem' }}>
        <FormLabel htmlFor="regPassword">{t('Password')}</FormLabel>
        <PasswordField
          id="regPassword"
          value={signUpPassword}
          onChange={(e) => setSignUpPassword(e.target.value)}
          placeholder={t('PasswordMinPlaceholder')}
          $hasError={!!signUpError}
        />
        {/* Jauge de force */}
        <PasswordStrength password={signUpPassword} />
      </FormGroup>

      {/* Confirmation du mot de passe */}
      <FormGroup style={{ marginTop: '0.75rem' }}>
        <FormLabel htmlFor="regConfirmPassword">{t('ConfirmPassword')}</FormLabel>
        <PasswordField
          id="regConfirmPassword"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder={t('RepeatPasswordPlaceholder')}
          $hasError={!!confirmPassword && confirmPassword !== signUpPassword}
        />
        {/* Indicateur live de non-correspondance (avant même la soumission) */}
        {!!confirmPassword && confirmPassword !== signUpPassword && (
          <InlineError role="alert" aria-live="polite" style={{ marginTop: '0.35rem' }}>
            {t('PasswordsDoNotMatch')}
          </InlineError>
        )}
      </FormGroup>

      {/* Erreur inline */}
      {signUpError && (
        <InlineError role="alert" aria-live="polite" style={{ marginTop: '0.5rem' }}>
          {signUpError}
        </InlineError>
      )}

      {/* Bouton d'inscription */}
      <PrimaryBtn
        type="submit"
        disabled={signUpLoading || !username || !signUpEmail || !signUpPassword || !confirmPassword || signUpPassword !== confirmPassword}
        style={{ marginTop: '1.25rem' }}
        aria-busy={signUpLoading}
      >
        {signUpLoading ? <Spinner style={{ marginBottom: 0, width: '1.25rem', height: '1.25rem' }} /> : null}
        {signUpLoading ? t('CreatingEllipsis') : t('CreateAccount')}
      </PrimaryBtn>

      {/* Lien connexion */}
      <p style={{ textAlign: 'center', marginTop: '0.75rem', fontSize: '0.8125rem' }}>
        {t('AlreadyHaveAccount')}{' '}
        <button
          type="button"
          onClick={onSwitchToLogin}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            color: 'inherit',
            fontWeight: 700,
            textDecoration: 'underline',
            fontSize: 'inherit',
          }}
        >
          {t('SignInAction')}
        </button>
      </p>
    </form>
  );
}

/* ─── Composant principal ───────────────────────────────────────────────────── */

/**
 * EmailAuthPanel
 *
 * @param {object}   props
 * @param {string}   props.currentState  — 'EMAIL_LOGIN' | 'EMAIL_REGISTER'
 * @param {function} props.dispatch      — dispatch useWalletStateMachine
 * @param {object}   props.emailAuth     — valeur de retour de useEmailAuth()
 */
export function EmailAuthPanel({ currentState, dispatch, emailAuth }) {
  const { t } = useTranslation();
  const isLogin    = currentState === 'EMAIL_LOGIN';
  const isRegister = currentState === 'EMAIL_REGISTER';

  /* ── Handlers navigation ──────────────────────────────────────────────────── */

  const handleBack = () => {
    dispatch({ type: ACTION_TYPES.BACK });
  };

  const handleSwitchToRegister = () => {
    dispatch({ type: ACTION_TYPES.SWITCH_TO_REGISTER });
  };

  const handleSwitchToLogin = () => {
    dispatch({ type: ACTION_TYPES.SWITCH_TO_LOGIN });
  };

  const handleForgotPassword = () => {
    dispatch({ type: ACTION_TYPES.SELECT_FORGOT_PASSWORD });
  };

  /* ── Rendu ──────────────────────────────────────────────────────────────────────
     Pas d'onglets : le choix Sign in / Create account est fait en amont dans le picker
     (variante A : 2 boutons distincts). La bascule reste possible via le lien discret en
     bas de chaque formulaire (onSwitchToRegister / onSwitchToLogin) + le Back vers le picker. */
  return (
    <>
      <WalletBody>
        {/* Lien retour */}
        <BackLink
          type="button"
          onClick={handleBack}
          aria-label={t('BackToWalletSelection')}
        >
          <BackArrow />
          {t('BackAction')}
        </BackLink>

        {/* Formulaire actif selon l'état */}
        {isLogin && (
          <SignInForm
            emailAuth={emailAuth}
            onBack={handleBack}
            onSwitchToRegister={handleSwitchToRegister}
            onForgotPassword={handleForgotPassword}
          />
        )}
        {isRegister && (
          <RegisterForm
            emailAuth={emailAuth}
            onSwitchToLogin={handleSwitchToLogin}
          />
        )}
      </WalletBody>

      <WalletFooter>
        <ToSText>
          By continuing, you agree to the{' '}
          <a href="/terms" target="_blank" rel="noopener noreferrer">
            terms of use
          </a>
          .
        </ToSText>
      </WalletFooter>
    </>
  );
}

export default EmailAuthPanel;
